import { IdentityProvider } from '@/client/accounts/types/account-user-management.type';
import { SimpleLockIcon } from '@/utils/helpers';
import classNames from 'classnames';
import { TFunction } from 'i18next';
import { get } from 'lodash';
import { InputText, InputTextProps } from 'primereact/inputtext';
import React, { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const getFormikInputTooltip = (
  tooltip: string | undefined,
  isSyncedField: boolean | undefined,
  isAdSyncedField: boolean | undefined,
  t: TFunction,
): string | undefined => {
  if (tooltip) {
    return tooltip;
  }

  if (!isSyncedField) {
    return undefined;
  }

  let identityProvider: IdentityProvider;

  if (isAdSyncedField) {
    identityProvider = IdentityProvider.MICROSOFT_ENTRA_ID;
  } else {
    identityProvider = IdentityProvider.GOOGLE_WORKSPACE;
  }

  return t('user.identityProvider.syncedField', { identityProvider });
};

const StyledDiv = styled.div`
  width: 100%;

  .p-tooltip-target-wrapper {
    width: 100%;
  }
`;

const StyledIcon = styled.i`
  margin-right: var(--xsmall-padding);
  position: relative;
  top: var(--xxxsmall-padding);
`;

export type FormikInputProps = {
  field: {
    name: string;
    value: string;
    onChange: () => void;
    onBlur: (e: SyntheticEvent<HTMLInputElement>) => void;
  };
  form: {
    touched: Record<string, any>;
    errors: Record<string, any>;
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean,
    ) => void;
  };
  label?: string;
  additionOnBlur?: () => void;
  inputState?: 'exists-active' | 'exists-inactive';
  inputStateMessage?: string;
  tooltip?: string;
  isSyncedField?: boolean;
  isAdSyncedField?: boolean;
};

export const FormikInput: React.FC<FormikInputProps & InputTextProps> = ({
  field,
  form,
  label,
  className,
  required,
  additionOnBlur,
  inputState,
  inputStateMessage,
  tooltip,
  isSyncedField,
  isAdSyncedField,
  ...rest
}) => {
  const fieldError = get(form.errors, field.name);
  const fieldTouched = get(form.touched, field.name);

  const { t } = useTranslation();

  return (
    <>
      {!!label && (
        <label
          htmlFor={field.name}
          className={classNames({
            'p-error': fieldTouched && !!fieldError,
            'p-exists-active':
              inputState === 'exists-active' && !(fieldTouched && !!fieldError),
            'p-exists-inactive':
              inputState === 'exists-inactive' &&
              !(fieldTouched && !!fieldError),
          })}
        >
          {isSyncedField && (
            <StyledIcon>
              <SimpleLockIcon />
            </StyledIcon>
          )}
          {label}
          {required ? <span className="red"> *</span> : ''}
        </label>
      )}
      <StyledDiv>
        <InputText
          {...field}
          value={field.value ?? ''}
          {...rest}
          tooltip={getFormikInputTooltip(
            tooltip,
            isSyncedField,
            isAdSyncedField,
            t,
          )}
          tooltipOptions={{
            position: 'right',
            showOnDisabled: true,
          }}
          disabled={isSyncedField || rest.disabled}
          className={classNames(className, {
            'p-invalid': fieldTouched && !!fieldError,
            'p-exists-active':
              inputState === 'exists-active' && !(fieldTouched && !!fieldError),
            'p-exists-inactive':
              inputState === 'exists-inactive' &&
              !(fieldTouched && !!fieldError),
          })}
          onBlur={(e: SyntheticEvent<HTMLInputElement>) => {
            field.onBlur(e);
            form.setFieldValue(field.name, field.value?.trim());

            if (additionOnBlur) {
              additionOnBlur();
            }
          }}
          autoComplete="off"
        />
      </StyledDiv>

      {fieldTouched && !!fieldError ? (
        <small className="p-error">{fieldError}</small>
      ) : inputState === 'exists-active' && inputStateMessage ? (
        <small className="p-exists-active">{inputStateMessage}</small>
      ) : inputState === 'exists-inactive' && inputStateMessage ? (
        <small className="p-exists-inactive">{inputStateMessage}</small>
      ) : null}
    </>
  );
};
